import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Box,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import {
  Mic as MicIcon,
  Add as AddIcon,
  Description as DescriptionIcon,
  HeadsetMic as HeadsetMicIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  DeleteOutline as DeleteOutlineIcon,
  MoreVert as MoreVertIcon
} from "@material-ui/icons";
import styled from "styled-components";
import { withRouter } from "react-router-dom";
import { withAlert } from "react-alert";
import { withTranslation } from "react-i18next";
import moment from "moment";
import Group from "../../waybee-ui/Group";
import Heading from "../../waybee-ui/Heading";
import BackButton from "../../components/BackButton";
import Button from "../../waybee-ui/Button";
import NewFeatureBadge from "../../components/NewFeatureBadge";
import {
  ATA_STATUS,
  deleteAtaDraftRemote,
  downloadAudioPlaceholder,
  downloadTranscript,
  filterAtasForContext,
  getAtaStatusLabel,
  listAtaDrafts,
  purgeDemoAtas,
  refreshAtaDraftsFromServer,
  resolveAtaStatus,
  searchAtas,
  ensureEncaminhamentosReleased,
  syncActionStatusesFromLaterMeetings
} from "./ataDraftStore";
import { deleteAtaAudio } from "./ataAudioStore";
import SmartMinutesService from "../../services/SmartMinutesService";
import AtaAudioPlayer from "./AtaAudioPlayer";
import AtaActionsGrouped from "./AtaActionsGrouped";
import AtaParticipantsChip from "./AtaParticipantsChip";
import AtaStatusChip from "./ataStatusChip";
import { speakersSummary } from "./speakers";
import { theme as appTheme } from "../../assets/theme/StyledTheme";

const FILTER_ALL = "all";
const FILTER_APPROVED = "aprovada";
const FILTER_AI = "processada_ia";

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const FilterChip = styled(Chip)`
  && {
    cursor: pointer;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    box-shadow: ${({ $active, theme }) =>
      $active ? `0 0 0 2px ${theme.color.primary.light}` : "none"};
  }
`;

const Card = styled.div`
  border: 1px solid #e8eef0;
  border-radius: 12px;
  padding: 16px 18px;
  margin-bottom: 12px;
  background: #fff;
  border-left: 4px solid
    ${({ $status, theme }) => {
      if ($status === ATA_STATUS.APROVADA) return theme.color.primary.main;
      if ($status === ATA_STATUS.PROCESSADA_IA) return "#ed6c02";
      if ($status === ATA_STATUS.VOZ_PROCESSADA) return "#0288d1";
      return "#90a4ae";
    }};
  cursor: pointer;
  transition: box-shadow 0.15s ease, border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.color.primary.light};
    box-shadow: 0 2px 12px ${({ theme }) => theme.color.primary.light};
    border-color: ${({ theme }) => theme.color.primary.main};
  }
`;

const Meta = styled(Typography)`
  && {
    color: #607681;
    font-size: 13px;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const CardMain = styled.div`
  min-width: 0;
  flex: 1 1 200px;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const MobileCollapseBtn = styled.button`
  display: none;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  margin: -6px -8px 0 0;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #78909c;
  cursor: pointer;

  @media (max-width: 720px) {
    display: inline-flex;
  }

  &:hover {
    background: ${({ theme }) => theme.color.primary.light};
    color: ${({ theme }) => theme.color.primary.dark};
  }
`;

const TurnsProgressWrap = styled.div`
  margin-top: 10px;
  max-width: 420px;
`;

const TurnsProgressMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 5px;
  font-size: 12px;
  color: #607681;

  strong {
    color: ${({ theme }) => theme.color.primary.dark};
    font-variant-numeric: tabular-nums;
  }
`;

const TurnsProgressTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background: #e8eef0;
  overflow: hidden;
`;

const TurnsProgressFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => `${Math.max(0, Math.min(100, $pct))}%`};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.color.primary.dark} 0%,
    ${({ theme }) => theme.color.primary.main} 100%
  );
  transition: width 0.25s ease;
`;

const CardControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex: 0 1 auto;
  max-width: 100%;
  /* Mantém à direita mesmo quando o flex-wrap quebra a linha do título */
  margin-left: auto;

  @media (max-width: 720px) {
    width: 100%;
    margin-left: 0;
  }
`;

const ActionRail = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  max-width: 100%;

  @media (max-width: 720px) {
    width: 100%;
    justify-content: flex-end;
  }
`;

const GhostAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid
    ${({ disabled, $active, theme }) => {
      if (disabled) return "#d7dee2";
      if ($active) return theme.color.primary.dark;
      return theme.color.primary.main;
    }};
  background: ${({ disabled, $active, theme }) => {
    if (disabled) return "#f5f7f8";
    if ($active) return theme.color.primary.dark;
    return "#fff";
  }};
  color: ${({ disabled, $active, theme }) => {
    if (disabled) return "#9aa7ad";
    if ($active) return "#fff";
    return theme.color.primary.dark;
  }};
  font-size: 13px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.color.primary.dark};
    border-color: ${({ theme }) => theme.color.primary.dark};
    color: #fff;

    .action-count {
      background: rgba(255, 255, 255, 0.22);
      color: #fff;
    }
  }
`;

const ActionCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $active, theme }) =>
    $active ? "#fff" : theme.color.primary.dark};
  background: ${({ $active, theme }) =>
    $active ? "rgba(255, 255, 255, 0.22)" : theme.color.primary.light};
`;

const SearchWrap = styled.div`
  position: relative;
  margin-top: 16px;
  max-width: 420px;

  .MuiSvgIcon-root {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #78909c;
    font-size: 20px;
    pointer-events: none;
  }

  .MuiFormControl-root {
    width: 100%;
  }

  .MuiOutlinedInput-input {
    padding-left: 40px;
  }
`;

const ActionsBlock = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 48, 59, 0.08);
`;

const ActionsBlockTitle = styled.div`
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #607681;
`;

class SmartMinutesList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      atas: [],
      filter: FILTER_ALL,
      search: "",
      expandedActions: {},
      isMobileUi: false,
      // No mobile: cards começam minimizados (resumo em uma linha).
      collapsedCards: {},
      deleteTarget: null,
      deleteConfirmText: "",
      deletingAta: false,
      cardMoreAnchor: null,
      cardMoreAtaId: null
    };
    this.mobileMq = null;
  }

  componentDidMount() {
    this.isMountedFlag = true;
    this.reload();
    this.onDraftsUpdated = () => this.reload();
    window.addEventListener("ata-drafts-updated", this.onDraftsUpdated);
    if (typeof window !== "undefined" && window.matchMedia) {
      this.mobileMq = window.matchMedia("(max-width: 720px)");
      this.onMobileMqChange = () => {
        const isMobileUi = !!this.mobileMq.matches;
        this.setState(prev => {
          if (!isMobileUi) return { isMobileUi };
          // Primeira entrada no mobile: minimiza cards ainda não tocados.
          const collapsedCards = { ...prev.collapsedCards };
          (prev.atas || []).forEach(a => {
            if (collapsedCards[a.id] === undefined) {
              collapsedCards[a.id] = true;
            }
          });
          return { isMobileUi, collapsedCards };
        });
      };
      this.onMobileMqChange();
      if (this.mobileMq.addEventListener) {
        this.mobileMq.addEventListener("change", this.onMobileMqChange);
      } else if (this.mobileMq.addListener) {
        this.mobileMq.addListener(this.onMobileMqChange);
      }
    }
  }

  componentWillUnmount() {
    this.isMountedFlag = false;
    window.removeEventListener("ata-drafts-updated", this.onDraftsUpdated);
    if (this.mobileMq && this.onMobileMqChange) {
      if (this.mobileMq.removeEventListener) {
        this.mobileMq.removeEventListener("change", this.onMobileMqChange);
      } else if (this.mobileMq.removeListener) {
        this.mobileMq.removeListener(this.onMobileMqChange);
      }
      this.mobileMq = null;
      this.onMobileMqChange = null;
    }
  }

  reload = async () => {
    const { match } = this.props;
    const classId = match.params.id || localStorage.getItem("class");
    const studentRa = match.params.studentId;
    purgeDemoAtas();
    // Cache local imediato; em seguida sync do servidor (não-demo)
    const localAtas = filterAtasForContext(listAtaDrafts(), {
      classId,
      studentRa
    });
    this.setState(prev => {
      const collapsedCards = { ...prev.collapsedCards };
      if (prev.isMobileUi) {
        localAtas.forEach(a => {
          if (collapsedCards[a.id] === undefined) {
            collapsedCards[a.id] = true;
          }
        });
      }
      return { atas: localAtas, collapsedCards };
    });
    const atas = await refreshAtaDraftsFromServer({ classId, studentRa });
    if (!this.isMountedFlag) return;
    // Reaplica fechamentos (done / parcial) nas atas anteriores a partir do retorno
    (atas || []).forEach(a => {
      if (!a || !a.id) return;
      ensureEncaminhamentosReleased(a.id);
      syncActionStatusesFromLaterMeetings(a.id);
    });
    const synced = filterAtasForContext(listAtaDrafts(), {
      classId,
      studentRa
    });
    this.setState(prev => {
      const collapsedCards = { ...prev.collapsedCards };
      if (prev.isMobileUi) {
        synced.forEach(a => {
          if (collapsedCards[a.id] === undefined) {
            collapsedCards[a.id] = true;
          }
        });
      }
      return { atas: synced, collapsedCards };
    });
  };

  toggleCardCollapsed = (ataId, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState(prev => ({
      collapsedCards: {
        ...prev.collapsedCards,
        [ataId]: !prev.collapsedCards[ataId]
      }
    }));
  };

  backTo = () => {
    const { match } = this.props;
    if (match.params.studentId) {
      return `/student/${match.params.studentId}`;
    }
    if (match.params.id) return `/classes/${match.params.id}`;
    return "/classes";
  };

  newPath = () => {
    const { match } = this.props;
    if (match.params.studentId) {
      return `/student/${match.params.studentId}/ata-inteligente/new`;
    }
    return `/classes/${match.params.id}/ata-inteligente/new`;
  };

  detailPath = ataId => {
    const { match } = this.props;
    if (match.params.studentId) {
      return `/student/${match.params.studentId}/ata-inteligente/${ataId}`;
    }
    return `/classes/${match.params.id}/ata-inteligente/${ataId}`;
  };

  setFilter = filter => {
    this.setState(prev => ({
      filter: prev.filter === filter ? FILTER_ALL : filter
    }));
  };

  onSearchChange = e => {
    this.setState({ search: e.target.value });
  };

  filteredAtas = () => {
    const { atas, filter, search } = this.state;
    let list = atas;
    if (filter === FILTER_APPROVED) {
      list = list.filter(a => resolveAtaStatus(a) === ATA_STATUS.APROVADA);
    } else if (filter === FILTER_AI) {
      list = list.filter(a => {
        const st = resolveAtaStatus(a);
        return (
          st === ATA_STATUS.PROCESSADA_IA ||
          st === ATA_STATUS.VOZ_PROCESSADA ||
          st === ATA_STATUS.APROVADA
        );
      });
    }
    return searchAtas(list, search);
  };

  onDownloadTranscript = ata => {
    const { alert, t } = this.props;
    downloadTranscript(ata);
    alert.show(t("smartMinutes.download.transcriptDone"), {
      title: t("smartMinutes.title")
    });
  };

  onDownloadAudio = async ata => {
    const { alert, t } = this.props;
    if (!ata.audioFileName && !ata.hasLocalAudio) {
      alert.show(t("smartMinutes.download.noAudio"), {
        title: t("common.actions.attention")
      });
      return;
    }
    const result = await downloadAudioPlaceholder(ata);
    alert.show(
      result && result.kind === "audio"
        ? t("smartMinutes.download.audioStarted")
        : t("smartMinutes.download.audioDemo"),
      { title: t("smartMinutes.title") }
    );
  };

  openDeleteDialog = (ata, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({
      deleteTarget: ata,
      deleteConfirmText: "",
      deletingAta: false
    });
  };

  closeDeleteDialog = () => {
    const { deletingAta } = this.state;
    if (deletingAta) return;
    this.setState({ deleteTarget: null, deleteConfirmText: "" });
  };

  onConfirmDelete = async () => {
    const { t, alert } = this.props;
    const { deleteTarget, deleteConfirmText, deletingAta } = this.state;
    if (!deleteTarget || deletingAta) return;
    if (
      String(deleteConfirmText || "").trim() !==
      t("smartMinutes.delete.confirmWord")
    ) {
      alert.show(t("smartMinutes.delete.helperQuoted"), {
        title: t("smartMinutes.delete.needed")
      });
      return;
    }
    this.setState({ deletingAta: true });
    try {
      if (deleteTarget.meetingId) {
        try {
          await SmartMinutesService.purgeMeetingVoice({
            meetingId: deleteTarget.meetingId,
            reason: "meeting_deleted"
          });
        } catch (purgeErr) {
          // eslint-disable-next-line no-console
          console.warn(
            "[ata] purge voice na exclusão falhou",
            purgeErr && purgeErr.message
          );
        }
      }
      await deleteAtaAudio(deleteTarget.id);
      await deleteAtaDraftRemote(deleteTarget.id, deleteTarget.meetingId);
      this.setState({
        deleteTarget: null,
        deleteConfirmText: "",
        deletingAta: false
      });
      await this.reload();
    } catch (e) {
      this.setState({ deletingAta: false });
      const apiMsg =
        e &&
        e.response &&
        e.response.data &&
        e.response.data.error &&
        e.response.data.error.message;
      alert.show(apiMsg || (e && e.message) || t("smartMinutes.delete.error"), {
        title: t("common.actions.error"),
        type: "error"
      });
    }
  };

  onOpenDetail = ata => {
    const { history } = this.props;
    history.push(this.detailPath(ata.id));
  };

  toggleActionsExpanded = (ataId, event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.setState(prev => ({
      expandedActions: {
        ...prev.expandedActions,
        [ataId]: !prev.expandedActions[ataId]
      }
    }));
  };

  renderActionsPanel = ata => {
    const { t } = this.props;
    const { expandedActions } = this.state;
    const actions = ata.actions || [];
    const priorReviews = (ata.priorActionReviews || []).map(r => ({
      id: r.actionId || r.id,
      description: r.description,
      assignee: r.assignee,
      status: r.status,
      statusNote: r.note || r.statusNote,
      audience: r.audience,
      ataTitle: r.ataTitle
    }));
    if (!actions.length && !priorReviews.length) return null;
    const open = !!expandedActions[ata.id];
    if (!open) return null;

    const hasValidated =
      ata.actionsValidated ||
      (actions || []).some(a => {
        const s = String((a && a.status) || "").toLowerCase();
        return (
          s === "done" ||
          s === "in_progress" ||
          s === "cancelled" ||
          s === "concluido" ||
          s === "parcial"
        );
      }) ||
      priorReviews.some(
        r =>
          r.status === "done" ||
          r.status === "in_progress" ||
          r.status === "cancelled"
      );

    return (
      <ActionsBlock
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        {priorReviews.length > 0 && (
          <>
            <ActionsBlockTitle>
              {t("smartMinutes.list.priorActionsTracking")}
            </ActionsBlockTitle>
            <AtaActionsGrouped
              ata={ata}
              actions={priorReviews}
              showStatus
              showOrigin
              muted
            />
          </>
        )}
        {actions.length > 0 && (
          <>
            <ActionsBlockTitle>
              {(() => {
                if (hasValidated && !priorReviews.length) {
                  return t("smartMinutes.list.actionsValidated");
                }
                if (priorReviews.length) {
                  return t("smartMinutes.action.fromThisMeeting");
                }
                return t("smartMinutes.list.actions");
              })()}
            </ActionsBlockTitle>
            <AtaActionsGrouped ata={ata} actions={actions} showStatus />
          </>
        )}
      </ActionsBlock>
    );
  };

  renderCardControls = ata => {
    const { t } = this.props;
    const { expandedActions, cardMoreAnchor, cardMoreAtaId } = this.state;
    const hasTranscript = !!ata.transcriptText;
    const hasAudio = !!(
      ata.audioFileName ||
      ata.hasLocalAudio ||
      ata.hasRemoteAudio ||
      ata.meetingId
    );
    const canPlayAudio = !!(
      ata.hasLocalAudio ||
      ata.hasRemoteAudio ||
      (ata.meetingId && ata.audioFileName)
    );
    const actions = ata.actions || [];
    const priorReviews = ata.priorActionReviews || [];
    const actionsOpen = !!expandedActions[ata.id];
    const actionsCount = actions.length + priorReviews.length;
    const moreOpen = cardMoreAtaId === ata.id && Boolean(cardMoreAnchor);

    return (
      <CardControls
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <ActionRail>
          {canPlayAudio && (
            <AtaAudioPlayer
              ataId={ata.id}
              meetingId={ata.meetingId}
              fileName={ata.audioFileName}
              variant="ghost"
              label={t("smartMinutes.audio.listen")}
            />
          )}
          {actionsCount > 0 && (
            <Tooltip
              title={
                actionsOpen
                  ? t("smartMinutes.list.hideActions")
                  : t("smartMinutes.list.actionsCount", {
                      count: actionsCount
                    })
              }
            >
              <span>
                <GhostAction
                  type="button"
                  $active={actionsOpen}
                  aria-expanded={actionsOpen}
                  onClick={e => this.toggleActionsExpanded(ata.id, e)}
                >
                  <AssignmentTurnedInIcon />
                  <ActionCountBadge
                    className="action-count"
                    $active={actionsOpen}
                  >
                    {actionsCount}
                  </ActionCountBadge>
                  {actionsOpen ? (
                    <ExpandLessIcon style={{ fontSize: 18 }} />
                  ) : (
                    <ExpandMoreIcon style={{ fontSize: 18 }} />
                  )}
                </GhostAction>
              </span>
            </Tooltip>
          )}
          <Tooltip title={t("smartMinutes.moreActions")}>
            <IconButton
              size="small"
              aria-label={t("smartMinutes.moreActions")}
              aria-haspopup="true"
              onClick={e => {
                e.stopPropagation();
                this.setState({
                  cardMoreAnchor: e.currentTarget,
                  cardMoreAtaId: ata.id
                });
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={moreOpen ? cardMoreAnchor : null}
            open={moreOpen}
            onClose={() =>
              this.setState({ cardMoreAnchor: null, cardMoreAtaId: null })}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            onClick={e => e.stopPropagation()}
          >
            {hasTranscript && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation();
                  this.setState({
                    cardMoreAnchor: null,
                    cardMoreAtaId: null
                  });
                  this.onDownloadTranscript(ata);
                }}
              >
                <ListItemIcon style={{ minWidth: 36 }}>
                  <DescriptionIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("smartMinutes.list.transcript")} />
              </MenuItem>
            )}
            {hasAudio && (
              <MenuItem
                onClick={e => {
                  e.stopPropagation();
                  this.setState({
                    cardMoreAnchor: null,
                    cardMoreAtaId: null
                  });
                  this.onDownloadAudio(ata);
                }}
              >
                <ListItemIcon style={{ minWidth: 36 }}>
                  <HeadsetMicIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={t("smartMinutes.list.audio")} />
              </MenuItem>
            )}
            <MenuItem
              onClick={e => {
                e.stopPropagation();
                this.setState({
                  cardMoreAnchor: null,
                  cardMoreAtaId: null
                });
                this.openDeleteDialog(ata, e);
              }}
            >
              <ListItemIcon style={{ minWidth: 36 }}>
                <DeleteOutlineIcon
                  fontSize="small"
                  style={{ color: "#c62828" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={t("common.actions.delete")}
                primaryTypographyProps={{ style: { color: "#c62828" } }}
              />
            </MenuItem>
          </Menu>
        </ActionRail>
        <AtaStatusChip ata={ata} status={ata.status} />
      </CardControls>
    );
  };

  render() {
    const {
      atas,
      filter,
      search,
      expandedActions,
      isMobileUi,
      collapsedCards,
      deleteTarget,
      deleteConfirmText,
      deletingAta
    } = this.state;
    const { history, match, t } = this.props;
    const visible = this.filteredAtas();
    const approved = atas.filter(
      a => resolveAtaStatus(a) === ATA_STATUS.APROVADA
    ).length;
    const aiProcessed = atas.filter(a => {
      const st = resolveAtaStatus(a);
      return (
        st === ATA_STATUS.PROCESSADA_IA ||
        st === ATA_STATUS.VOZ_PROCESSADA ||
        st === ATA_STATUS.APROVADA
      );
    }).length;
    const emptyMessage = search.trim()
      ? t("smartMinutes.list.emptySearch")
      : t("smartMinutes.list.emptyFilter");
    const fromStudentProfile = !!(
      match &&
      match.params &&
      match.params.studentId
    );
    const introText = fromStudentProfile
      ? t("smartMinutes.list.helpStudent")
      : t("smartMinutes.list.helpClass");
    const deleteConfirmed =
      String(deleteConfirmText || "").trim() ===
      t("smartMinutes.delete.confirmWord");

    return (
      <Grid container spacing={2}>
        <BackButton to={this.backTo()} />

        <Grid item xs={12}>
          <Group>
            <TitleRow>
              <TitleLeft>
                <MicIcon
                  style={{ color: appTheme.color.primary.dark, fontSize: 36 }}
                />
                <Heading level={1} gutterBottom={false}>
                  {t("smartMinutes.title")}
                </Heading>
                <NewFeatureBadge />
              </TitleLeft>
              <Button
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => history.push(this.newPath())}
              >
                {t("smartMinutes.list.new")}
              </Button>
            </TitleRow>
            <Typography variant="body1" style={{ marginTop: 8, color: "#555" }}>
              {introText}
            </Typography>
            <SearchWrap>
              <SearchIcon />
              <TextField
                variant="outlined"
                size="small"
                placeholder={t("smartMinutes.list.search")}
                value={search}
                onChange={this.onSearchChange}
                inputProps={{ "aria-label": t("smartMinutes.list.searchAria") }}
              />
            </SearchWrap>
            <Box mt={2} display="flex" style={{ gap: 8 }} flexWrap="wrap">
              <FilterChip
                size="small"
                clickable
                $active={filter === FILTER_ALL}
                label={t("smartMinutes.list.records", { count: atas.length })}
                variant={filter === FILTER_ALL ? "default" : "outlined"}
                color={filter === FILTER_ALL ? "primary" : "default"}
                onClick={() => this.setFilter(FILTER_ALL)}
              />
              <Tooltip title={t("smartMinutes.status.approvedHint")}>
                <span>
                  <FilterChip
                    size="small"
                    clickable
                    $active={filter === FILTER_APPROVED}
                    label={t("smartMinutes.list.approvedCount", {
                      count: approved
                    })}
                    onClick={() => this.setFilter(FILTER_APPROVED)}
                    style={{
                      background:
                        filter === FILTER_APPROVED
                          ? appTheme.color.primary.main
                          : appTheme.color.primary.light,
                      color:
                        filter === FILTER_APPROVED
                          ? "#fff"
                          : appTheme.color.primary.dark
                    }}
                  />
                </span>
              </Tooltip>
              <Tooltip title={t("smartMinutes.status.aiProcessedHint")}>
                <span>
                  <FilterChip
                    size="small"
                    clickable
                    $active={filter === FILTER_AI}
                    label={t("smartMinutes.list.aiProcessedCount", {
                      count: aiProcessed
                    })}
                    onClick={() => this.setFilter(FILTER_AI)}
                    style={{
                      background: filter === FILTER_AI ? "#ed6c02" : "#fff3e0",
                      color: filter === FILTER_AI ? "#fff" : "#e65100"
                    }}
                  />
                </span>
              </Tooltip>
            </Box>
          </Group>
        </Grid>

        <Grid item xs={12}>
          <Group>
            <Heading level={2} gutterBottom>
              {t("smartMinutes.list.title")}
            </Heading>
            {!visible.length && (
              <Typography color="textSecondary">{emptyMessage}</Typography>
            )}
            {visible.map(ata => {
              const summary = speakersSummary(
                ata.speakers,
                ata.transcriptText,
                ata.turnLinks
              );
              const turnsPct = summary.turnsTotal
                ? Math.round((summary.turnsLinked / summary.turnsTotal) * 100)
                : 0;
              const actionsOpen = !!expandedActions[ata.id];
              const cardMinimized = isMobileUi && !!collapsedCards[ata.id];
              const actionsCount = (ata.actions || []).length;
              const displayStatus = resolveAtaStatus(ata);
              const compactParts = [
                moment(ata.createdAt).format("DD/MM/YYYY HH:mm"),
                summary.turnsTotal
                  ? t("smartMinutes.list.turnsCompact", {
                      linked: summary.turnsLinked,
                      total: summary.turnsTotal,
                      pct: turnsPct
                    })
                  : null,
                actionsCount > 0
                  ? t("smartMinutes.list.encShort", { count: actionsCount })
                  : null,
                getAtaStatusLabel(displayStatus) || displayStatus
              ].filter(Boolean);

              return (
                <Card
                  key={ata.id}
                  $status={displayStatus}
                  role="button"
                  tabIndex={0}
                  onClick={() => this.onOpenDetail(ata)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      this.onOpenDetail(ata);
                    }
                  }}
                >
                  <CardTop>
                    <CardMain>
                      <CardTitleRow>
                        <Typography variant="subtitle1" component="div">
                          <strong>{ata.title}</strong>
                        </Typography>
                        <MobileCollapseBtn
                          type="button"
                          aria-expanded={!cardMinimized}
                          aria-label={
                            cardMinimized
                              ? t("smartMinutes.list.expandCard")
                              : t("smartMinutes.list.collapseCard")
                          }
                          onClick={e => this.toggleCardCollapsed(ata.id, e)}
                        >
                          {cardMinimized ? (
                            <ExpandMoreIcon />
                          ) : (
                            <ExpandLessIcon />
                          )}
                        </MobileCollapseBtn>
                      </CardTitleRow>
                      {cardMinimized ? (
                        <Meta style={{ marginTop: 2 }}>
                          {compactParts.join(" · ")}
                        </Meta>
                      ) : (
                        <>
                          <Meta>
                            {moment(ata.createdAt).format("DD/MM/YYYY HH:mm")}
                            {ata.entryMode
                              ? ` · ${
                                  t(`smartMinutes.entryMode.${ata.entryMode}`) !==
                                  `smartMinutes.entryMode.${ata.entryMode}`
                                    ? t(`smartMinutes.entryMode.${ata.entryMode}`)
                                    : ata.entryMode
                                }`
                              : ""}
                            {ata.audioFileName ? ` · ${ata.audioFileName}` : ""}
                          </Meta>
                          <Meta style={{ marginTop: 6 }}>
                            <AtaParticipantsChip ata={ata} size="small" />
                          </Meta>
                        </>
                      )}
                    </CardMain>
                    {!cardMinimized && this.renderCardControls(ata)}
                  </CardTop>
                  {!cardMinimized && ata.actionsValidated && (
                    <Box
                      mt={1.25}
                      display="flex"
                      style={{ gap: 6 }}
                      flexWrap="wrap"
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t("smartMinutes.list.actionsValidatedChip")}
                      />
                    </Box>
                  )}
                  {!cardMinimized && summary.turnsTotal > 0 && (
                    <TurnsProgressWrap>
                      <TurnsProgressMeta>
                        <span>{t("smartMinutes.list.turnsConfirmed")}</span>
                        <strong>
                          {summary.turnsLinked}
                          /
                          {summary.turnsTotal}
                          {" ("}
                          {turnsPct}
                          %)
                        </strong>
                      </TurnsProgressMeta>
                      <TurnsProgressTrack>
                        <TurnsProgressFill $pct={turnsPct} />
                      </TurnsProgressTrack>
                    </TurnsProgressWrap>
                  )}
                  <Collapse in={actionsOpen && !cardMinimized}>
                    {this.renderActionsPanel(ata)}
                  </Collapse>
                </Card>
              );
            })}
          </Group>
        </Grid>

        <Dialog
          open={!!deleteTarget}
          onClose={this.closeDeleteDialog}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("smartMinutes.delete.title")}</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {t("smartMinutes.delete.body")}
            </DialogContentText>
            <DialogContentText>
              {t("smartMinutes.delete.voicesNote")}
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              fullWidth
              variant="outlined"
              label={t("smartMinutes.delete.confirmation")}
              placeholder={t("smartMinutes.delete.confirmWord")}
              helperText={t("smartMinutes.delete.helper")}
              value={deleteConfirmText}
              onChange={e =>
                this.setState({ deleteConfirmText: e.target.value })}
              disabled={deletingAta}
              onKeyDown={e => {
                if (e.key === "Enter" && deleteConfirmed && !deletingAta) {
                  this.onConfirmDelete();
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.closeDeleteDialog}
              disabled={deletingAta}
              color="default"
            >
              {t("common.actions.cancel")}
            </Button>
            <Button
              onClick={this.onConfirmDelete}
              disabled={!deleteConfirmed || deletingAta}
              color="primary"
              variant="contained"
            >
              {deletingAta
                ? t("smartMinutes.delete.deleting")
                : t("common.actions.delete")}
            </Button>
          </DialogActions>
        </Dialog>
      </Grid>
    );
  }
}

SmartMinutesList.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
      studentId: PropTypes.string
    })
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func
  }).isRequired,
  alert: PropTypes.shape({
    show: PropTypes.func
  }).isRequired,
  t: PropTypes.func.isRequired
};

export default withAlert()(withRouter(withTranslation()(SmartMinutesList)));
